<template>
    <div class="h-full flex flex-col">
        <div v-if="loading" class="flex-1 flex items-center justify-center min-h-[400px]">
            <div class="animate-pulse text-gray-400">Loading selection...</div>
        </div>

        <div v-else-if="error" class="flex-1 flex items-center justify-center min-h-[400px] text-red-500">
            {{ error }}
        </div>

        <div v-else class="flex flex-col h-full">
            <!-- Header -->
            <div class="px-6 py-8 border-b border-gray-100 bg-white">
            <div class="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 class="text-3xl font-light text-gray-900">{{ cart.name }}</h1>
                    <p class="text-sm text-gray-500 mt-2 uppercase tracking-widest">{{ items.length }} Items</p>
                </div>

                <div class="flex items-center gap-4 self-start md:self-center">
                    <!-- Edit Collection Button (Owner Only) -->
                    <a 
                        v-if="isOwner && editUrl"
                        :href="editUrl"
                        class="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded hover:bg-black transition"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487zm0 0L19.5 7.125" />
                        </svg>
                        Edit Collection
                    </a>

                    <!-- View Toggle -->
                    <div class="flex rounded border border-gray-200 p-0.5 bg-gray-50">
                        <button 
                            @click="viewMode = 'grid'"
                            class="p-2 rounded transition"
                            :class="viewMode === 'grid' ? 'bg-white shadow text-gray-900' : 'text-gray-400 hover:text-gray-600'"
                            title="Grid View"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z" />
                            </svg>
                        </button>
                        <button 
                            @click="viewMode = 'list'"
                            class="p-2 rounded transition"
                            :class="viewMode === 'list' ? 'bg-white shadow text-gray-900' : 'text-gray-400 hover:text-gray-600'"
                            title="List View"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M8.25 6.75h12M8.25 12h12m-12 5.25h12M3.75 6.75h.007v.008H3.75V6.75zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zM3.75 12h.007v.008H3.75V12zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zM3.75 17.25h.007v.008H3.75v-.008zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
                            </svg>
                        </button>
                    </div>
                </div>
            </div>

            <!-- Notes -->
            <div v-if="cart.notes" class="mt-6">
                <p class="text-sm whitespace-pre-line">{{ cart.notes }}</p>
            </div>
        </div>

        <div v-if="items.length === 0" class="flex-1 flex flex-col items-center justify-center text-gray-400 min-h-[400px]">
            <p>This selection is empty.</p>
        </div>
        
        <div v-else class="bg-gray-50/50 flex-1 p-6">
            <!-- Grid View -->
            <div v-if="viewMode === 'grid'" class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                <div v-for="item in items" :key="item.id" class="bg-white group rounded-lg shadow-sm hover:shadow-md transition overflow-hidden flex flex-col border border-gray-100">
                    <div class="aspect-[4/3] bg-gray-100 relative overflow-hidden">
                        <template v-if="item.is_document">
                            <a v-if="item.redirect_url" :href="item.redirect_url" target="_blank" class="flex w-full h-full flex-col items-center justify-center transition" :class="documentBgClass(item.file_type)">
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1" stroke="currentColor" class="w-12 h-12 mb-2" :class="documentIconClass(item.file_type)">
                                  <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                                </svg>
                                <span class="text-xs font-bold uppercase tracking-wider" :class="documentTextClass(item.file_type)">{{ documentLabel(item.file_type) }}</span>
                                <span class="text-xs mt-1" :class="documentSubTextClass(item.file_type)">Click to open</span>
                            </a>
                            <div v-else class="flex w-full h-full flex-col items-center justify-center" :class="documentBgBaseClass(item.file_type)">
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1" stroke="currentColor" class="w-12 h-12 mb-2" :class="documentIconClass(item.file_type)">
                                  <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                                </svg>
                                <span class="text-xs font-bold uppercase tracking-wider" :class="documentTextClass(item.file_type)">{{ documentLabel(item.file_type) }}</span>
                            </div>
                        </template>
                        <template v-else>
                            <button
                                v-if="isImageOnlyLink(item)"
                                type="button"
                                class="w-full h-full relative block cursor-zoom-in focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-gray-900"
                                :aria-label="`View larger image of ${item.name}`"
                                @click="openLightbox(item)"
                            >
                                <img :src="item.photo_url" :alt="item.name" class="w-full h-full object-cover group-hover:scale-105 transition duration-500">
                                <span class="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition pointer-events-none"></span>
                                <span class="absolute bottom-2 right-2 rounded bg-black/60 px-2 py-1 text-[10px] font-medium uppercase tracking-wider text-white opacity-0 group-hover:opacity-100 transition">
                                    View image
                                </span>
                            </button>
                            <template v-else>
                                <img v-if="item.photo_url" :src="item.photo_url" :alt="item.name" class="w-full h-full object-cover group-hover:scale-105 transition duration-500">
                                <div v-else class="w-full h-full flex items-center justify-center text-gray-300 bg-gray-50">No Image</div>

                                <a v-if="item.redirect_url" :href="item.redirect_url" target="_blank" @click="handleLinkClick($event, item)" class="absolute inset-0 z-10"></a>
                            </template>
                        </template>
                    </div>
                    <div class="p-4 flex flex-col flex-1">
                        <div class="flex justify-between items-start mb-2">
                            <h3 class="font-medium text-gray-900 leading-tight">
                                <a v-if="item.redirect_url" :href="item.redirect_url" target="_blank" @click="handleLinkClick($event, item)" class="hover:underline">{{ item.name }}</a>
                                <span v-else>{{ item.name }}</span>
                            </h3>
                        </div>
                        <div class="flex items-end justify-between border-t border-gray-50">
                            <div class="text-sm text-gray-500">
                                <div class="flex gap-2">
                                    <template v-if="item.is_internal">
                                        <p class="text-sm font-light text-gray-900">Size: {{ item.size_info }}</p>
                                    </template>
                                    <template v-else>
                                        <p class="text-blue-600 font-medium">WEB LINK</p>
                                    </template>
                                    <div v-if="showQty" class="text-sm font-light text-gray-900">
                                        Quantity: {{ item.quantity }}
                                    </div>
                                </div>
                                <div v-if="item.lot_number" class="text-xs text-gray-400 mt-1 flex items-center gap-1">
                                    <span>Lot: {{ item.lot_number }}</span>
                                    <button 
                                        @click.stop="copyToClipboard(item.lot_number, $event)"
                                        class="text-gray-300 hover:text-gray-600 transition"
                                        title="Copy Lot Number"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-3.5 h-3.5 clipboard pointer-events-none">
                                          <path stroke-linecap="round" stroke-linejoin="round" d="M15.666 3.888A2.25 2.25 0 0013.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 01-.75.75H9a.75.75 0 01-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 01-2.25 2.25H6.75A2.25 2.25 0 014.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 011.927-.184" />
                                        </svg>
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-3.5 h-3.5 check hidden text-green-500 pointer-events-none">
                                          <path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                                        </svg>
                                    </button>
                                </div>

                                <div v-if="item.notes" class="mt-2 text-sm italic text-gray-600 border-t border-gray-100 pt-1">
                                    {{ item.notes }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- List View -->
            <div v-if="viewMode === 'list'" class="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
                <table class="w-full text-left">
                    <thead class="bg-gray-50 border-b border-gray-200 text-xs font-bold text-gray-400 uppercase tracking-widest">
                        <tr>
                            <th class="px-6 py-3 w-24">Image</th>
                            <th class="px-6 py-3">Item</th>
                            <th class="px-6 py-3">Size/Details</th>
                            <th v-if="showQty" class="px-6 py-3 w-24 text-center">Qty</th>
                        </tr>
                    </thead>
                    <tbody class="divide-y divide-gray-100 selection-list">
                        <tr v-for="item in items" :key="item.id" class="hover:bg-gray-50/50 transition">
                            <td class="px-6 py-4">
                                <div class="w-16 h-12 bg-gray-100 rounded overflow-hidden relative">
                                    <template v-if="item.is_document">
                                        <a v-if="item.redirect_url" :href="item.redirect_url" target="_blank" class="flex w-full h-full flex-col items-center justify-center transition rounded" :class="documentBgClass(item.file_type)">
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6" :class="documentIconClass(item.file_type)">
                                              <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                                            </svg>
                                            <span class="text-[8px] font-bold uppercase mt-0.5" :class="documentTextClass(item.file_type)">{{ documentLabelShort(item.file_type) }}</span>
                                        </a>
                                        <div v-else class="flex w-full h-full flex-col items-center justify-center rounded" :class="documentBgBaseClass(item.file_type)">
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6" :class="documentIconClass(item.file_type)">
                                              <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                                            </svg>
                                            <span class="text-[8px] font-bold uppercase mt-0.5" :class="documentTextClass(item.file_type)">{{ documentLabelShort(item.file_type) }}</span>
                                        </div>
                                    </template>
                                    <template v-else>
                                        <button
                                            v-if="isImageOnlyLink(item)"
                                            type="button"
                                            class="w-full h-full relative block cursor-zoom-in focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-900"
                                            :aria-label="`View larger image of ${item.name}`"
                                            @click="openLightbox(item)"
                                        >
                                            <img :src="item.photo_url" :alt="item.name" class="w-full h-full object-cover">
                                        </button>
                                        <img v-else-if="item.photo_url" :src="item.photo_url" :alt="item.name" class="w-full h-full object-cover">
                                        <div v-else class="w-full h-full flex items-center justify-center text-xs text-gray-300">N/A</div>
                                    </template>
                                </div>
                            </td>
                            <td class="px-6 py-4">
                                <h3 class="font-medium text-gray-900">
                                    <a v-if="item.redirect_url" :href="item.redirect_url" target="_blank" @click="handleLinkClick($event, item)" class="hover:underline">{{ item.name }}</a>
                                    <span v-else>{{ item.name }}</span>
                                </h3>
                                <span v-if="!item.is_internal" class="text-[10px] font-bold text-blue-600 uppercase mt-1 inline-block">Web Link</span>
                            </td>
                            <td class="px-6 py-4 text-sm text-gray-500">
                                <template v-if="item.is_internal">
                                    <div>{{ item.size_info }}</div>
                                    <div v-if="item.lot_number" class="text-xs text-gray-400 mt-0.5 flex items-center gap-1">
                                        <span>Lot: {{ item.lot_number }}</span>
                                        <button 
                                            @click.stop="copyToClipboard(item.lot_number, $event)"
                                            class="text-gray-300 hover:text-gray-600 transition"
                                            title="Copy Lot Number"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-3.5 h-3.5 clipboard pointer-events-none">
                                              <path stroke-linecap="round" stroke-linejoin="round" d="M15.666 3.888A2.25 2.25 0 0013.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 01-.75.75H9a.75.75 0 01-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 01-2.25 2.25H6.75A2.25 2.25 0 014.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 011.927-.184" />
                                            </svg>
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-3.5 h-3.5 check hidden text-green-500 pointer-events-none">
                                              <path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                                            </svg>
                                        </button>
                                    </div>
                                </template>
                                <div v-if="item.notes" class="mt-1 text-xs italic text-gray-600">
                                    Note: {{ item.notes }}
                                </div>
                            </td>
                            <td v-if="showQty" class="px-6 py-4 text-center text-lg font-light">
                                {{ item.quantity }}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>

    <!-- Image lightbox (link items with image but no URL) -->
    <div
        v-if="lightboxItem"
        class="fixed inset-0 z-[60] flex items-center justify-center bg-black/90 p-4 md:p-8"
        role="dialog"
        aria-modal="true"
        :aria-label="lightboxItem.name"
        @click.self="closeLightbox"
    >
        <button
            type="button"
            class="absolute top-4 right-4 rounded-full bg-white/10 p-2 text-white hover:bg-white/20 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
            aria-label="Close image"
            @click="closeLightbox"
        >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
        </button>
        <div class="flex max-h-full max-w-full flex-col items-center gap-4">
            <img
                :src="lightboxItem.photo_url"
                :alt="lightboxItem.name"
                class="max-h-[85vh] max-w-full object-contain shadow-2xl"
            >
            <p v-if="lightboxItem.name" class="max-w-2xl text-center text-sm text-white/80">{{ lightboxItem.name }}</p>
        </div>
    </div>

    <!-- External Link Confirmation Modal -->
    <div v-if="showExternalModal" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" @click.self="cancelNavigation">
        <div class="bg-white rounded-lg shadow-xl max-w-md w-full p-6 animate-in fade-in zoom-in duration-200">
            <h3 class="text-lg font-medium text-gray-900 mb-2">Leave the website?</h3>
            <p class="text-sm text-gray-500 mb-6">
                You are about to navigate to an external website:
                <br>
                <span class="font-mono text-xs bg-gray-50 p-1 rounded mt-2 block break-all">{{ pendingExternalUrl }}</span>
            </p>
            <div class="flex justify-end gap-3">
                <button 
                    @click="cancelNavigation"
                    class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                >
                    Cancel
                </button>
                <button 
                    @click="proceedNavigation"
                    class="px-4 py-2 text-sm font-medium text-white bg-gray-900 border border-transparent rounded hover:bg-black focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900"
                >
                    Continue
                </button>
            </div>
        </div>
    </div>
</div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';

const props = defineProps({
    token: {
        type: String,
        required: true
    }
});

const cart = ref({});
const items = ref([]);
const loading = ref(true);
const error = ref(null);
const viewMode = ref('grid');
const showQty = ref(!!document.querySelector('meta[name="show-qty"]'));
const isOwner = ref(false);

const collectionPage = document.querySelector('meta[name="collection-page"]')?.getAttribute('content') || '';
const editUrl = computed(() => {
    if (!isOwner.value || !collectionPage || !cart.value?.id) return null;
    return `${collectionPage}#selectionID=${cart.value.id}`;
});

// External Link Modal State
const showExternalModal = ref(false);
const pendingExternalUrl = ref('');

// Image lightbox (items with a photo but no clickable destination URL)
const lightboxItem = ref(null);

const isImageOnlyLink = (item) => {
    if (!item?.photo_url || item.is_document) {
        return false;
    }

    if (item.redirect_url) {
        return false;
    }

    // external/site-product = web links; custom = image upload without URL
    return ['external', 'site-product', 'custom'].includes(item.item_type);
};

const openLightbox = (item) => {
    if (!isImageOnlyLink(item)) {
        return;
    }

    lightboxItem.value = item;
};

const closeLightbox = () => {
    lightboxItem.value = null;
};

const onLightboxKeydown = (event) => {
    if (event.key === 'Escape') {
        closeLightbox();
    }
};

const confirmNavigation = (url) => {
    pendingExternalUrl.value = url;
    showExternalModal.value = true;
};

const cancelNavigation = () => {
    showExternalModal.value = false;
    pendingExternalUrl.value = '';
};

const proceedNavigation = () => {
    if (pendingExternalUrl.value) {
        window.open(pendingExternalUrl.value, '_blank');
        showExternalModal.value = false;
        pendingExternalUrl.value = '';
    }
};

const handleLinkClick = (event, item) => {
    if (!item.is_internal) {
        event.preventDefault();
        confirmNavigation(item.redirect_url);
    }
};

const copyToClipboard = async (text, event) => {
    if (!text) return;
    try {
        await navigator.clipboard.writeText(text);
        if (event && event.currentTarget) {
            const btn = event.currentTarget;
            const clipboardIcon = btn.querySelector('svg.clipboard');
            const checkIcon = btn.querySelector('svg.check');
            
            if (clipboardIcon && checkIcon) {
                clipboardIcon.classList.add('hidden');
                checkIcon.classList.remove('hidden');
                setTimeout(() => {
                    clipboardIcon.classList.remove('hidden');
                    checkIcon.classList.add('hidden');
                }, 2000);
            }
        }
    } catch (err) {
        console.error('Failed to copy text: ', err);
        prompt("Copy this text:", text);
    }
};

// Document display helpers
const documentLabel = (fileType) => {
    const labels = { pdf: 'PDF Document', excel: 'Excel Spreadsheet', powerpoint: 'PowerPoint Presentation', word: 'Word Document' };
    return labels[fileType] || 'Document';
};
const documentLabelShort = (fileType) => {
    const labels = { pdf: 'PDF', excel: 'XLS', powerpoint: 'PPT', word: 'DOC' };
    return labels[fileType] || 'DOC';
};
const documentBgClass = (fileType) => {
    const classes = { pdf: 'bg-red-50 hover:bg-red-100', excel: 'bg-green-50 hover:bg-green-100', powerpoint: 'bg-orange-50 hover:bg-orange-100', word: 'bg-blue-50 hover:bg-blue-100' };
    return classes[fileType] || 'bg-gray-50 hover:bg-gray-100';
};
const documentBgBaseClass = (fileType) => {
    const classes = { pdf: 'bg-red-50', excel: 'bg-green-50', powerpoint: 'bg-orange-50', word: 'bg-blue-50' };
    return classes[fileType] || 'bg-gray-50';
};
const documentIconClass = (fileType) => {
    const classes = { pdf: 'text-red-400', excel: 'text-green-500', powerpoint: 'text-orange-400', word: 'text-blue-400' };
    return classes[fileType] || 'text-gray-400';
};
const documentTextClass = (fileType) => {
    const classes = { pdf: 'text-red-500', excel: 'text-green-600', powerpoint: 'text-orange-500', word: 'text-blue-500' };
    return classes[fileType] || 'text-gray-500';
};
const documentSubTextClass = (fileType) => {
    const classes = { pdf: 'text-red-400', excel: 'text-green-400', powerpoint: 'text-orange-400', word: 'text-blue-400' };
    return classes[fileType] || 'text-gray-400';
};

const fetchData = async () => {    
    loading.value = true;
    try {
        const apiUrlMeta = document.querySelector('meta[name="api-url"]');
        const baseUrl = apiUrlMeta ? apiUrlMeta.content : '';
        const url = baseUrl ? `${baseUrl}/favorites-public/${props.token}` : `/api/favorites-public/${props.token}`;
        
        const headers = { 'Accept': 'application/json' };
        const apiToken = localStorage.getItem('api_token');
        if (apiToken) {
            headers['Authorization'] = `Bearer ${apiToken}`;
        }

        const response = await fetch(url, { headers });
        if (!response.ok) throw new Error('Failed to load selection');
        const data = await response.json();
        cart.value = data.cart;
        isOwner.value = !!data.is_owner;
        items.value = data.items;
    } catch (e) {
        console.error(e);
        error.value = "Failed to load selection.";
    } finally {
        loading.value = false;
    }
};

onMounted(() => {
    fetchData();
    document.addEventListener('keydown', onLightboxKeydown);
});

onUnmounted(() => {
    document.removeEventListener('keydown', onLightboxKeydown);
});
</script>
